import { fromUtf8, utf8 } from "./bytes";
import { HermesError } from "./errors";

/** 经线路发出的一个 HTTP 请求（路径 + query 由隧道转给主机上的 hermes serve）。 */
export interface HttpRequest {
  method: string;
  /** 例如 `/api/status`、`/api/fs/download?path=...` */
  path: string;
  headers?: Record<string, string>;
  body?: Uint8Array;
  /** 秒 */
  timeout?: number;
  signal?: AbortSignal;
}

export interface HttpResponse {
  status: number;
  /** 键全部小写；同名头合并成逗号分隔的一行 */
  headers: Record<string, string>;
  body: Uint8Array;
}

export type SocketFrame = { text: string } | { binary: Uint8Array };

/** 对话 WebSocket（直连或隧道里的一条）。 */
export interface GatewaySocket {
  send(text: string): Promise<void>;
  sendBinary?(data: Uint8Array): Promise<void>;
  /** 下一帧文本；连接关闭时抛错 */
  receive(): Promise<string>;
  receiveFrame?(): Promise<SocketFrame>;
  close(): void;
}

/**
 * 到一个 Hermes 网关的“线路”：HTTP 请求、WebSocket 都经由它发出。
 * - RelayTunnel：经 TimiBot 中转、端到端加密的隧道
 * - DemoGateway：体验模式，本地的示例数据
 */
export interface HermesTransport {
  request(req: HttpRequest): Promise<HttpResponse>;
  openSocket(path: string, timeout: number): Promise<GatewaySocket>;
}

export function jsonBody(value: unknown): Uint8Array {
  return utf8(JSON.stringify(value));
}

export function parseJSON(res: HttpResponse): unknown {
  try {
    return JSON.parse(fromUtf8(res.body));
  } catch {
    return undefined;
  }
}

export function ensureOK(res: HttpResponse, fallback = "请求失败"): void {
  if (res.status >= 200 && res.status < 300) return;
  throw httpError(res, fallback);
}

export function httpError(res: HttpResponse, fallback: string): HermesError {
  let message = fallback;
  const json = parseJSON(res) as Record<string, unknown> | undefined;
  if (json && typeof json === "object") {
    const detail = json["detail"] ?? json["error"] ?? json["message"];
    if (typeof detail === "string") message = detail;
  }
  return HermesError.http(res.status, message);
}

/** 拼接路径和 query（值已编码） */
export function withQuery(path: string, query?: Record<string, string | undefined>): string {
  if (!query) return path;
  const pairs = Object.entries(query).filter(([, v]) => v !== undefined) as [string, string][];
  if (pairs.length === 0) return path;
  return path + "?" + pairs.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
}
